/**
 * Login Form Module
 * Business logic for the login form
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLogin, isSuperAdmin } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { useNavigate } from 'react-router';
import { Spinner } from '@/components/ui/spinner';
import { Link } from 'react-router';

const loginSchema = z.object({
  email: z.email('Please enter a valid email address').min(1, 'Email is required').max(254, 'Email is too long'),
  password: z.string().min(1, 'Password is required').min(6, 'Password must be at least 6 characters').max(128, 'Password is too long')
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const navigate = useNavigate();
  const loginMutation = useLogin();

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await loginMutation.mutateAsync({
        email: data.email,
        password: data.password
      });

      toast.success('Login successful!');

      // Wait a tick to ensure state updates are propagated
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (onSuccess) {
        onSuccess();
      } else {
        // Redirect based on user type (super admin vs regular user)
        // Check custom_attributes from login response
        const userIsSuperAdmin = isSuperAdmin(result.user);
        if (userIsSuperAdmin) {
          navigate('/super-admin/accounts', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to login. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type='email' placeholder='Enter your email' autoComplete='email' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <div className='flex items-center justify-between'>
                <FormLabel>Password</FormLabel>
                <Link to='/forgot-password' className='text-sm text-primary hover:underline'>
                  Forgot password?
                </Link>
              </div>
              <FormControl>
                <Input type='password' placeholder='Enter your password' autoComplete='current-password' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type='submit' className='w-full' variant={'outline'} disabled={loginMutation.isPending}>
          {loginMutation.isPending ? (
            <>
              <Spinner className='mr-2' />
              Logging in...
            </>
          ) : (
            'Login'
          )}
        </Button>
      </form>
    </Form>
  );
}
